import type { ArticleDocument, ApplicationDocument, ClientDocument, FeedbackDocument, SupportTicketDocument, UserDocument, SearchResults } from "@/lib/search";

export const EMPTY_RESULTS: SearchResults = {
  articles: [],
  applications: [],
  clients: [],
  feedback: [],
  supportTickets: [],
  escalations: [],
  users: [],
};

// How many hits each search group shows inline before falling back to
// "See all".
export const PREVIEW_COUNT = 5;

export const ARTICLE_TYPE_LABELS: Record<string, string> = {
  TECHNICAL: "Technical",
  VALUE: "Value",
  SUCCESS_STORY: "Success Story",
  SOP: "SOP",
};

export type FlatItem = {
  key: string;
  href: string;
  title: string;
  subtitle: string | null;
  group: "Articles" | "Applications" | "Clients" | "Support Tickets" | "Feedback" | "Users";
};

export function toFlatItems(results: SearchResults): FlatItem[] {
  const articles: FlatItem[] = results.articles.slice(0, PREVIEW_COUNT).map((a: ArticleDocument) => ({
    key: `article-${a.id}`,
    href: `/articles/${a.id}`,
    title: a.processName,
    subtitle: [a.applicationName, a.clientName].filter(Boolean).join(" · ") || null,
    group: "Articles",
  }));
  const applications: FlatItem[] = results.applications
    .slice(0, PREVIEW_COUNT)
    .map((a: ApplicationDocument) => ({
      key: `application-${a.id}`,
      href: `/applications/${a.id}`,
      title: a.name,
      subtitle: a.description,
      group: "Applications",
    }));
  const clients: FlatItem[] = results.clients.slice(0, PREVIEW_COUNT).map((c: ClientDocument) => ({
    key: `client-${c.id}`,
    href: `/clients/${c.id}`,
    title: c.name,
    subtitle: c.notes,
    group: "Clients",
  }));
  const supportTickets: FlatItem[] = results.supportTickets
    .slice(0, PREVIEW_COUNT)
    .map((t: SupportTicketDocument) => ({
      key: `ticket-${t.id}`,
      href: `/support-tickets/${t.id}`,
      title: t.title,
      subtitle: t.creatorName,
      group: "Support Tickets",
    }));
  const feedback: FlatItem[] = results.feedback.slice(0, PREVIEW_COUNT).map((f: FeedbackDocument) => ({
    key: `feedback-${f.id}`,
    href: "/feedback",
    title: f.message,
    subtitle: f.creatorName,
    group: "Feedback",
  }));
  const users: FlatItem[] = results.users.slice(0, PREVIEW_COUNT).map((u: UserDocument) => ({
    key: `user-${u.id}`,
    href: `/users/${u.id}`,
    title: u.name,
    subtitle: u.roleName,
    group: "Users",
  }));
  return [...articles, ...applications, ...clients, ...supportTickets, ...feedback, ...users];
}
